import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Refresh, DollarSign, Cpu, Activity, Zap } from "@/components/icons";
import { Button } from "@/components/ui/button";

import { fetchBillingTotals } from "@/lib/api";
import { Stat } from "@/components/Stat";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

export function BillingPage() {
  const q = useQuery({
    queryKey: ["billing", "system_totals"],
    queryFn: () => fetchBillingTotals(),
    refetchInterval: 30_000,
  });

  const d: any = q.data ?? {};

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Billing Summary</h1>
          <div className="text-sm opacity-70">System-wide billing totals</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <Refresh className={`h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {q.isLoading ? (
        <div className="flex justify-center p-12">
          <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : q.isError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">Failed to load billing totals</div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Stat
              title="Total Revenue"
              value={`$${(d.total_revenue ?? 0).toFixed(2)}`}
              description="all-time revenue"
              icon={<DollarSign className="h-4 w-4 opacity-60" />}
            />
            <Stat
              title="Total Chargers"
              value={d.units_with_data ?? 0}
              description="units with billing data"
              icon={<Cpu className="h-4 w-4 opacity-60" />}
            />
            <Stat
              title="Total Sessions"
              value={d.total_sessions ?? 0}
              description="completed + active"
              icon={<Activity className="h-4 w-4 opacity-60" />}
            />
            <Stat
              title="Total Energy"
              value={`${(d.total_energy_kwh ?? 0).toFixed(2)} kWh`}
              description="energy delivered"
              icon={<Zap className="h-4 w-4 opacity-60" />}
            />
          </div>

          {d.cache_timestamp && (
            <div className="text-xs opacity-50 text-center">
              Cache updated: {new Date(d.cache_timestamp).toLocaleString()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
